/* eslint-disable no-console */
import { useState, useCallback, useRef, useEffect } from 'react'

import debounce from 'lodash.debounce'

import axios from 'axios'

// Fetches query-scoped suggestions for the AI-mode search dropdown, split by product type.
// Response shape: { data: { cg: [[phrase, { thumbnailUrl, itemTitle, total }]], print_ready: [...] } }.
// Returns { results, isLoading }. `results` is null until a non-empty query resolves, so the dropdown
// falls back to its curated columns. `isLoading` is true from the first keystroke of a non-empty
// query until its response settles, so the dropdown can show a fetching state instead of looking
// stale while the (slow) backend query resolves.
export default function useGroupedLiveSearch({ query = '', enabled = false } = {}) {
  const [results, setResults] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const currentRequest = useRef(null)
  const requestCounter = useRef(0)

  const fetchGrouped = useCallback(
    debounce(async (value) => {
      requestCounter.current += 1
      const currentRequestId = requestCounter.current

      if (currentRequest.current) {
        currentRequest.current.cancel()
      }

      const cancelToken = axios.CancelToken.source()
      currentRequest.current = cancelToken

      try {
        const response = await axios.get('/api/internal/items/grouped_live_search', {
          params: { query: value },
          headers: { 'Cache-Control': 'no-cache' },
          cancelToken: cancelToken.token,
          timeout: 3000,
        })

        if (currentRequestId !== requestCounter.current) return

        setResults(response.data?.data || null)
        setIsLoading(false)
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error fetching grouped suggestions:', error)
          // A cancelled request is always superseded by a newer one that has re-set the loading
          // flag, so only a real failure clears it here.
          setIsLoading(false)
        }
      }
    }, 300),
    []
  )

  useEffect(() => {
    const trimmed = query.trim()

    if (!enabled || !trimmed) {
      fetchGrouped.cancel()
      setResults(null)
      setIsLoading(false)
      return undefined
    }

    // Set before the debounce fires so the fetching state also covers the 300ms debounce window;
    // otherwise the dropdown flashes the stale/curated columns at the start of every keystroke.
    setIsLoading(true)

    // Invalidate the in-flight request now, not when the next debounce fires: otherwise an old
    // response landing inside the new debounce window passes the request-id guard, clears loading,
    // and exposes stale results while the newer request is still active.
    if (currentRequest.current) {
      currentRequest.current.cancel()
    }

    fetchGrouped(trimmed)

    return () => fetchGrouped.cancel()
  }, [query, enabled, fetchGrouped])

  useEffect(() => {
    return () => {
      if (currentRequest.current) {
        currentRequest.current.cancel()
      }
    }
  }, [])

  return { results, isLoading }
}
